import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Star, MapPin, Search, Store as StoreIcon } from "lucide-react";

function StoreCard({ s }) {
  return (
    <Link to={`/app/store/${s.id}`} data-testid={`store-card-${s.id}`}
      className="group rounded-2xl border bg-card overflow-hidden hover:shadow-lg hover:border-brand/40 transition-all">
      <div className="h-36 bg-muted overflow-hidden relative">
        {s.cover ? <img src={s.cover} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          : <div className="w-full h-full flex items-center justify-center"><StoreIcon className="w-8 h-8 text-muted-foreground" /></div>}
        {s.is_open && <span className="absolute top-3 left-3 bg-emerald-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">OPEN</span>}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display font-bold leading-tight">{s.name}</h3>
          <div className="flex items-center gap-1 text-sm shrink-0"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />{s.rating}</div>
        </div>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.description}</p>
        <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.city}</span>
          <Badge variant="outline" className="text-[10px]">{s.category}</Badge>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const [stores, setStores] = useState([]);
  const [cats, setCats] = useState([]);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (active) params.category = active;
    if (q) params.q = q;
    if (country) params.country = country;
    const { data } = await api.get("/stores", { params });
    setStores(data);
    setLoading(false);
  };

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); api.get("/countries").then((r) => setCountries(r.data)); }, []);
  useEffect(() => { const timer = setTimeout(load, 250); return () => clearTimeout(timer); /* eslint-disable-next-line */ }, [q, active, country]);

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-slate-950 text-white p-6 sm:p-10 relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="absolute -top-16 end-0 w-72 h-72 bg-brand/25 rounded-full blur-[100px]" />
        <div className="relative">
          <h1 className="text-3xl sm:text-4xl font-display font-black leading-tight max-w-lg">{t("customer.heroTitle")}</h1>
          <div className="flex flex-col sm:flex-row gap-3 mt-6 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input data-testid="store-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("customer.searchStores")}
                className="ps-12 pe-4 h-14 rounded-2xl bg-white text-slate-900 border-0 text-base" />
            </div>
            <select data-testid="country-filter" value={country} onChange={(e) => setCountry(e.target.value)}
              className="h-14 px-4 rounded-2xl bg-white text-slate-900 border-0 text-base font-medium">
              <option value="">{t("customer.allCountries")}</option>
              {countries.map((c) => <option key={c.code} value={c.code}>{c.name} ({c.currency})</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        <button data-testid="cat-all" onClick={() => setActive(null)}
          className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border ${!active ? "bg-brand text-white border-brand" : "bg-card hover:border-brand/40"}`}>{t("common.all")}</button>
        {cats.map((c) => (
          <button key={c.id} data-testid={`cat-${c.id}`} onClick={() => setActive(c.name)}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border ${active === c.name ? "bg-brand text-white border-brand" : "bg-card hover:border-brand/40"}`}>{c.name}</button>
        ))}
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => <div key={i} className="h-64 rounded-2xl bg-muted animate-pulse" />)}
        </div>
      ) : stores.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">{t("customer.noStores")}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {stores.map((s) => <StoreCard key={s.id} s={s} />)}
        </div>
      )}
    </div>
  );
}
